// Recognition review sets: frozen capture units reviewed together.
import {
  RecognitionReviewSetCreateRequest,
  RecognitionReviewSetDetail,
  RecognitionReviewSetListResponse,
  RecognitionReviewSetPreviewRequest,
  RecognitionReviewSetPreviewResponse,
} from '../../types/recognitionReview';
import { forgeRequest, forgeVoidRequest } from '../forgeRequest';
import { FORGE_PR_REVIEW_API_URL } from './endpoints';

const REVIEW_SETS_URL = `${FORGE_PR_REVIEW_API_URL}/review-sets`;

/** Matches a pasted mod list against one store's capture units in a date range. */
export async function previewRecognitionReviewSet(
  token: string,
  request: RecognitionReviewSetPreviewRequest
): Promise<RecognitionReviewSetPreviewResponse> {
  return forgeRequest<RecognitionReviewSetPreviewResponse>(
    token,
    `${REVIEW_SETS_URL}/preview`,
    'Error previewing the review set',
    { method: 'POST', body: request }
  );
}

/** Freezes the chosen units into a set and stamps them with the set's sampling strategy. */
export async function createRecognitionReviewSet(
  token: string,
  request: RecognitionReviewSetCreateRequest
): Promise<RecognitionReviewSetDetail> {
  return forgeRequest<RecognitionReviewSetDetail>(
    token,
    REVIEW_SETS_URL,
    'Error saving the review set',
    { method: 'POST', body: request }
  );
}

export async function fetchRecognitionReviewSets(
  token: string,
  chainId: number
): Promise<RecognitionReviewSetListResponse> {
  const query = new URLSearchParams({ chain_id: String(chainId) });
  return forgeRequest<RecognitionReviewSetListResponse>(
    token,
    `${REVIEW_SETS_URL}?${query.toString()}`,
    'Error loading review sets',
    { dedupeKey: ['fetchRecognitionReviewSets', `${token}:${chainId}`] }
  );
}

/** One set with each unit's boxes, scans, reviews and reviewers as they are now. */
export async function fetchRecognitionReviewSet(
  token: string,
  chainId: number,
  reviewSetId: string
): Promise<RecognitionReviewSetDetail> {
  const query = new URLSearchParams({ chain_id: String(chainId) });
  return forgeRequest<RecognitionReviewSetDetail>(
    token,
    `${REVIEW_SETS_URL}/${encodeURIComponent(reviewSetId)}?${query.toString()}`,
    'Error loading the review set'
  );
}

/** Deletes the set only; its units keep the sampling strategy they were stamped with. */
export async function deleteRecognitionReviewSet(
  token: string,
  chainId: number,
  reviewSetId: string
): Promise<void> {
  const query = new URLSearchParams({ chain_id: String(chainId) });
  return forgeVoidRequest(
    token,
    `${REVIEW_SETS_URL}/${encodeURIComponent(reviewSetId)}?${query.toString()}`,
    'Error deleting the review set',
    { method: 'DELETE' }
  );
}
